import { Injectable } from '@angular/core';
import { filterNil } from '@ngneat/elf';
import { isEqual } from 'lodash-es';
import {
  Observable,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  combineLatest,
  startWith,
  tap,
  shareReplay,
} from 'rxjs';
import { PanelView } from '../sessions/panel/panel.component';
import { UserEvent, UserEventType } from '../models/session-sync';
import { SpaceRepository } from '../state/space.repository';
import {
  ISpaceLeaderMode,
  LeaderModeRepositoryService,
} from '../state/leader-mode-repository.service';
import { SessionsSyncService } from './sessions-sync.service';
import { UserService } from './user.service';
import { TelemetryService } from './telemetry.service';
import { SessionSharedDataService } from './session-shared-data.service';

export interface ISpaceLeaderModeUI extends ISpaceLeaderMode {
  currentUserIsLeader: boolean;
  currentUserIsLeaderInAnotherTab: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SpaceLeaderModeService {
  // used to log when leader mode state or view changed
  private _lastLeaderModeState?: ISpaceLeaderMode;

  constructor(
    private spaceRepo: SpaceRepository,
    private userService: UserService,
    private sessionSyncService: SessionsSyncService,
    private leaderModeRepositoryService: LeaderModeRepositoryService,
    private telemetry: TelemetryService,
    private sessionSharedDataService: SessionSharedDataService,
  ) {}

  public readonly activeSpaceLeaderModeState$: Observable<ISpaceLeaderModeUI> =
    this.leaderModeRepositoryService.spaceLeaderMode$.pipe(
      distinctUntilChanged(isEqual),
      tap((state: ISpaceLeaderMode) => {
        this.telemetry.setSessionVars({
          leader_mode_active: state.leaderModeEnabled,
          leader_mode_active_is_leader: this.isCurrentUserLeader(state),
        });
      }),
      map(this.constructSpaceLeaderModeStateUi.bind(this)),
      shareReplay({ bufferSize: 1, refCount: true }),
      tap((state) => {
        if (this.isCurrentUserBeingLed(state)) {
          this.sessionSharedDataService.closeLeftPanelIfOpen(PanelView.appSelector);
        }
      }),
    );

  public readonly leaderModeLeaderUserChange$: Observable<ISpaceLeaderModeUI> =
    this.activeSpaceLeaderModeState$.pipe(distinctUntilKeyChanged('currentLeaderTabId'));

  public readonly currentUserIsBeingLed$: Observable<boolean> =
    this.activeSpaceLeaderModeState$.pipe(
      map((leaderModeState: ISpaceLeaderModeUI) => this.isCurrentUserBeingLed(leaderModeState)),
    );

  public readonly currentUserIsLeader$: Observable<boolean> = this.activeSpaceLeaderModeState$.pipe(
    map((leaderModeState: ISpaceLeaderModeUI) => this.isCurrentUserLeader(leaderModeState)),
  );

  public readonly currentHostIsBeingLed$: Observable<boolean> = combineLatest([
    this.activeSpaceLeaderModeState$,
    this.spaceRepo.isCurrentUserHost$,
  ]).pipe(
    map(
      ([leaderModeState, isCurrentUserHost]) =>
        isCurrentUserHost && this.isCurrentUserBeingLed(leaderModeState),
    ),
    distinctUntilChanged(),
  );

  public readonly activeSpaceLeaderModeLocation$: Observable<UserEvent | undefined> =
    this.sessionSyncService.userEvents$.pipe(
      filterNil(),
      filter((userEvent) => {
        if (!this.spaceRepo.activeSpace) {
          return false;
        }
        const leaderId = this.getSpaceLeaderModeStateUi().currentLeaderUid;
        return (
          leaderId === userEvent?.userId &&
          userEvent?.type === UserEventType.LOCATION &&
          userEvent.location?.vpt !== undefined
        );
      }),
    );

  readonly leaderModeViewModel$ = combineLatest([
    this.activeSpaceLeaderModeState$,
    this.leaderModeLeaderUserChange$.pipe(startWith(null)),
  ]).pipe(
    map(([activeSpaceLeaderModeState, currentLeaderModeState]) => {
      const leader = this.spaceRepo.activeSpace?.populatedUsers.find(
        (user) => user._id === currentLeaderModeState?.currentLeaderUid,
      );

      return {
        activeSpaceLeaderModeState,
        leader,
      };
    }),
  );

  public readonly leaderModeActive$ = this.leaderModeViewModel$.pipe(
    map((state) => Boolean(state.activeSpaceLeaderModeState.currentLeaderUid)),
  );

  public currentUserIsBeingLed(): boolean {
    return this.isCurrentUserBeingLed(this.leaderModeRepositoryService.getCurrentState());
  }

  public currentUserIsLeader(): boolean {
    return this.isCurrentUserLeader(this.leaderModeRepositoryService.getCurrentState());
  }

  public getSpaceLeaderModeStateUi(): ISpaceLeaderModeUI {
    return this.constructSpaceLeaderModeStateUi(this.leaderModeRepositoryService.getCurrentState());
  }

  public isCurrentUserLeader(spaceLeaderModeState: ISpaceLeaderMode): boolean {
    if (!spaceLeaderModeState.currentLeaderTabId) {
      return false;
    }
    return (
      spaceLeaderModeState.currentLeaderTabId === this.userService.userUniqueHash ||
      spaceLeaderModeState.currentLeaderTabId === this.sessionSyncService.getSocketId()
    );
  }

  private isCurrentUserBeingLed(leaderModeState: ISpaceLeaderMode): boolean {
    return (
      leaderModeState.currentLeaderUid !== undefined && !this.isCurrentUserLeader(leaderModeState)
    );
  }

  private constructSpaceLeaderModeStateUi(
    spaceLeaderModeState: ISpaceLeaderMode,
  ): ISpaceLeaderModeUI {
    if (
      !this._lastLeaderModeState ||
      this._lastLeaderModeState.currentLeaderUid !== spaceLeaderModeState.currentLeaderUid ||
      this._lastLeaderModeState.leaderSpaceView !== spaceLeaderModeState.leaderSpaceView
    ) {
      this.telemetry.event('[leaderMode State Change]', {
        spaceLeaderModeState,
      });
    }
    this._lastLeaderModeState = spaceLeaderModeState;
    return {
      ...spaceLeaderModeState,
      currentUserIsLeader: this.isCurrentUserLeader(spaceLeaderModeState),
      currentUserIsLeaderInAnotherTab: this.isUserLeaderInAnotherTab(spaceLeaderModeState),
    };
  }

  private isUserLeaderInAnotherTab(spaceLeaderModeState: ISpaceLeaderMode): boolean {
    return (
      spaceLeaderModeState.currentLeaderUid === this.userService.user.value?.user?._id &&
      !this.isCurrentUserLeader(spaceLeaderModeState)
    );
  }
}
